import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { BookOpenCheck, Clock, Compass, Hand } from 'lucide-react';
import { hapticSelection } from '../../lib/haptics';
import { useIsDesktop } from '../../lib/useIsDesktop';
import LecturesCollectives from './LecturesCollectives';
import Spiritualite from './Spiritualite';
import { Tasbih } from '../../components/culte/Tasbih';
import { Qibla } from '../../components/culte/Qibla';

/**
 * Hub Spirituel — onglets pensés pour le mobile :
 *  1. Prières         → horaires de prière du jour (onglet par défaut)
 *  2. Tasbih & Qibla  → compteur de dhikr + boussole Qibla
 *  3. Lectures        → lectures collectives (Kamil)
 *
 * Sur desktop, « Lectures » est masqué (déjà présent dans la barre latérale).
 */
const TABS = [
  { id: 'prieres', label: 'Prières', icon: Clock },
  { id: 'outils', label: 'Tasbih & Qibla', icon: Compass, labelClass: 'text-[11px] leading-tight' },
  { id: 'lectures', label: 'Lectures', icon: BookOpenCheck, desktopHidden: true },
];

export default function SpiritualiteHub() {
  const isDesktop = useIsDesktop();
  const tabs = TABS.filter((t) => !(isDesktop && t.desktopHidden));
  const tabIds = tabs.map((t) => t.id);

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(requested || 'prieres');
  // Onglet effectif : repli si l'onglet demandé est inconnu ou masqué (desktop).
  const activeTab = tabIds.includes(tab) ? tab : tabIds[0];

  const onChange = (value) => {
    hapticSelection();
    setTab(value);
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <Tabs value={activeTab} onValueChange={onChange} className="space-y-4">
      <TabsList className={`grid w-full ${tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} h-auto p-1 rounded-2xl bg-muted shadow-sm ring-1 ring-border/50 md:sticky md:top-0 z-30 backdrop-blur`}>
        {tabs.map((t) => (
          <TabsTrigger key={t.id} value={t.id} className={`flex-col gap-1 py-2 rounded-xl data-[state=active]:shadow ${t.labelClass || 'text-xs'}`}>
            <t.icon className="h-4 w-4" />
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Onglet 1 — Heures de prière (hero de date + horaires) */}
      <TabsContent value="prieres" className="mt-0 focus-visible:outline-none">
        <Spiritualite />
      </TabsContent>

      {/* Onglet 2 — Tasbih & Qibla */}
      <TabsContent value="outils" className="mt-0 focus-visible:outline-none">
        {/* Mobile : bascule Tasbih / Qibla ; grand écran : côte à côte. */}
        <div className="lg:hidden">
          <Tabs defaultValue="tasbih" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="tasbih" className="flex items-center gap-2">
                <Hand className="h-4 w-4" />
                Tasbih
              </TabsTrigger>
              <TabsTrigger value="qibla" className="flex items-center gap-2">
                <Compass className="h-4 w-4" />
                Qibla
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tasbih" className="mt-4">
              <Tasbih />
            </TabsContent>
            <TabsContent value="qibla" className="mt-4">
              <Qibla />
            </TabsContent>
          </Tabs>
        </div>
        <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
          <Tasbih />
          <Qibla />
        </div>
      </TabsContent>

      {/* Onglet 3 — Lectures collectives */}
      <TabsContent value="lectures" className="mt-0 focus-visible:outline-none">
        <LecturesCollectives />
      </TabsContent>
    </Tabs>
  );
}
